import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { ChevronLeft, Settings, Save, Info, Lock, User, Mail, Building2, KeyRound, Copy } from 'lucide-react';
import { toast } from 'sonner';

const API = `/api`;

const SettingsPage = ({ slpData }) => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [changingPassword, setChangingPassword] = useState(false);
  const [passwordForm, setPasswordForm] = useState({ current: '', newPw: '', confirm: '' });
  const [settings, setSettings] = useState({
    threshold_count: 2,
    threshold_level: 'significant'
  });

  useEffect(() => {
    const fetchSettings = async () => {
      try {
        const response = await axios.get(`${API}/settings`, { withCredentials: true });
        setSettings({
          threshold_count: response.data.threshold_count,
          threshold_level: response.data.threshold_level
        });
      } catch (error) {
        console.error('Failed to load settings:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchSettings();
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/settings`, settings, { withCredentials: true });
      toast.success('Settings saved successfully!');
    } catch (error) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const handleChangePassword = async () => {
    if (!passwordForm.current || !passwordForm.newPw || !passwordForm.confirm) {
      toast.error('Please fill in all password fields'); return;
    }
    if (passwordForm.newPw !== passwordForm.confirm) {
      toast.error('New passwords do not match'); return;
    }
    if (passwordForm.newPw.length < 8) {
      toast.error('New password must be at least 8 characters'); return;
    }
    setChangingPassword(true);
    try {
      await axios.post(`${API}/slp/change-password`, {
        current_password: passwordForm.current,
        new_password: passwordForm.newPw
      }, { withCredentials: true });
      toast.success('Password changed successfully');
      setPasswordForm({ current: '', newPw: '', confirm: '' });
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to change password');
    } finally {
      setChangingPassword(false);
    }
  };

  const thresholdOptions = [
    { value: 1, label: '1 of 3 areas', desc: 'Lower bar — flags referral if any single impact area qualifies' },
    { value: 2, label: '2 of 3 areas', desc: 'Default — flags referral when two or more impact areas qualify' },
    { value: 3, label: '3 of 3 areas', desc: 'Highest bar — only flags when all three impact areas qualify' }
  ];

  const levelOptions = [
    { value: 'significant', label: 'Significant only', desc: 'Only "Significant Impact" counts toward the threshold' },
    { value: 'moderate', label: 'Moderate or higher', desc: '"Moderate" and "Significant" both count toward the threshold' }
  ];

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: 'var(--fe-bg)' }}>
        <div className="inline-block w-12 h-12 border-4 border-[#2C4C3B] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen py-8 px-4" style={{ backgroundColor: 'var(--fe-bg)' }}>
      <div className="max-w-3xl mx-auto">
        <div className="mb-8">
          <button
            data-testid="back-to-dashboard-from-settings"
            onClick={() => navigate('/dashboard')}
            className="inline-flex items-center gap-2 text-[#595959] hover:text-[#1C1C1C] mb-4 transition-colors"
          >
            <ChevronLeft className="w-4 h-4" />
            Back to Dashboard
          </button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 bg-[#2C4C3B] rounded-2xl flex items-center justify-center">
              <Settings className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Referral Settings</h1>
              <p className="text-sm text-gray-600">{slpData?.organization_name || 'My School'}</p>
            </div>
          </div>
        </div>

        {/* Profile Section */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8 mb-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#2C4C3B]/10 flex items-center justify-center">
              <User className="w-5 h-5 text-[#2C4C3B]" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>Your Profile</h2>
              <p className="text-sm text-[#595959]">Account and school information</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div>
              <label className="block text-xs font-semibold text-[#595959] uppercase tracking-wider mb-1.5">
                <User className="w-3.5 h-3.5 inline mr-1.5" />Name
              </label>
              <p className="text-[#1C1C1C] font-medium" data-testid="profile-name">{slpData?.slp_name || slpData?.email || '—'}</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#595959] uppercase tracking-wider mb-1.5">
                <Mail className="w-3.5 h-3.5 inline mr-1.5" />Email
              </label>
              <p className="text-[#1C1C1C] font-medium" data-testid="profile-email">{slpData?.email || '—'}</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#595959] uppercase tracking-wider mb-1.5">
                <Building2 className="w-3.5 h-3.5 inline mr-1.5" />School
              </label>
              <p className="text-[#1C1C1C] font-medium" data-testid="profile-school">{slpData?.organization_name || '—'}</p>
            </div>
            <div>
              <label className="block text-xs font-semibold text-[#595959] uppercase tracking-wider mb-1.5">
                <KeyRound className="w-3.5 h-3.5 inline mr-1.5" />School Code
              </label>
              <div className="flex items-center gap-2">
                <span className="text-[#2C4C3B] font-mono font-semibold tracking-widest" data-testid="profile-join-code">{slpData?.join_code || '—'}</span>
                {slpData?.join_code && (
                  <button
                    onClick={() => { navigator.clipboard.writeText(slpData.join_code); toast.success('School code copied!'); }}
                    className="p-1 rounded-lg hover:bg-[#F3F4F1] transition-colors"
                    title="Copy code"
                  >
                    <Copy className="w-3.5 h-3.5 text-[#595959]" />
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Threshold Settings */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8 space-y-8">
          <div className="bg-blue-50 border border-blue-200 rounded-2xl p-4 flex gap-3">
            <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
            <div className="text-sm text-blue-800">
              <p className="font-semibold mb-1">How thresholds work</p>
              <p>When a teacher completes a referral, each of the 3 impact areas (Classroom Grades, Testing Results, Social Communication) is rated. If enough areas meet your chosen level, the tool recommends a <strong>Special Education evaluation path</strong> instead of <strong>Classroom Support</strong>.</p>
            </div>
          </div>

          <div>
            <h2 className="text-xl font-bold text-gray-900 mb-2">How many areas must qualify?</h2>
            <p className="text-sm text-gray-600 mb-4">Choose how many of the 3 impact areas need to meet the level to recommend more direct interventions and/or potential special education evaluation.</p>
            <div className="space-y-3">
              {thresholdOptions.map((option) => (
                <button
                  key={option.value}
                  data-testid={`threshold-count-${option.value}`}
                  onClick={() => setSettings(prev => ({ ...prev, threshold_count: option.value }))}
                  className={`w-full p-5 rounded-2xl border-2 transition-all text-left ${
                    settings.threshold_count === option.value
                      ? 'border-[#2C4C3B] bg-[#2C4C3B]/5'
                      : 'border-[#E5E5E5] hover:border-[#E5E5E5]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{option.label}</h3>
                      <p className="text-sm text-gray-600 mt-1">{option.desc}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      settings.threshold_count === option.value
                        ? 'border-[#2C4C3B] bg-[#2C4C3B]'
                        : 'border-[#E5E5E5]'
                    }`}>
                      {settings.threshold_count === option.value && (
                        <div className="w-2 h-2 bg-white rounded-full" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-[#E5E5E5] pt-8">
            <h2 className="text-xl font-bold text-gray-900 mb-2">What impact level qualifies?</h2>
            <p className="text-sm text-gray-600 mb-4">Decide whether only "Significant" ratings count, or if "Moderate" should also trigger a referral.</p>
            <div className="space-y-3">
              {levelOptions.map((option) => (
                <button
                  key={option.value}
                  data-testid={`threshold-level-${option.value}`}
                  onClick={() => setSettings(prev => ({ ...prev, threshold_level: option.value }))}
                  className={`w-full p-5 rounded-2xl border-2 transition-all text-left ${
                    settings.threshold_level === option.value
                      ? 'border-[#2C4C3B] bg-[#2C4C3B]/5'
                      : 'border-[#E5E5E5] hover:border-[#E5E5E5]'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-gray-900">{option.label}</h3>
                      <p className="text-sm text-gray-600 mt-1">{option.desc}</p>
                    </div>
                    <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                      settings.threshold_level === option.value
                        ? 'border-[#2C4C3B] bg-[#2C4C3B]'
                        : 'border-[#E5E5E5]'
                    }`}>
                      {settings.threshold_level === option.value && (
                        <div className="w-2 h-2 bg-white rounded-full" />
                      )}
                    </div>
                  </div>
                </button>
              ))}
            </div>
          </div>

          <div className="border-t border-[#E5E5E5] pt-6 flex justify-end">
            <button
              data-testid="save-settings-button"
              onClick={handleSave}
              disabled={saving}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium transition-colors disabled:bg-[#E5E5E5] disabled:text-[#595959]"
            >
              <Save className="w-5 h-5" />
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        </div>

        {/* Change Password */}
        <div className="bg-white rounded-2xl border border-[#E5E5E5] p-8 mt-6">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-xl bg-[#2C4C3B]/10 flex items-center justify-center">
              <Lock className="w-5 h-5 text-[#2C4C3B]" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-[#1C1C1C]" style={{ fontFamily: 'Outfit, sans-serif' }}>Change Password</h2>
              <p className="text-sm text-[#595959]">Update your account password</p>
            </div>
          </div>
          <div className="space-y-4 max-w-md">
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">Current Password</label>
              <input
                type="password"
                data-testid="current-password-input"
                value={passwordForm.current}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, current: e.target.value }))}
                placeholder="Enter current password"
                className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">New Password</label>
              <input
                type="password"
                data-testid="new-password-input"
                value={passwordForm.newPw}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, newPw: e.target.value }))}
                placeholder="Min 8 characters"
                className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-[#1C1C1C] mb-1.5">Confirm New Password</label>
              <input
                type="password"
                data-testid="confirm-password-input"
                value={passwordForm.confirm}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirm: e.target.value }))}
                placeholder="Confirm new password"
                className="w-full h-11 px-4 rounded-xl border border-[#E5E5E5] bg-[#FAFAF8] text-sm focus:ring-2 focus:ring-[#2C4C3B]/30 focus:border-[#2C4C3B] outline-none transition-all"
              />
            </div>
            <button
              data-testid="change-password-button"
              onClick={handleChangePassword}
              disabled={changingPassword}
              className="flex items-center gap-2 px-6 py-3 rounded-xl bg-[#2C4C3B] hover:bg-[#1E3629] text-white font-medium transition-colors disabled:bg-[#E5E5E5] disabled:text-[#595959]"
            >
              <Lock className="w-4 h-4" />
              {changingPassword ? 'Changing...' : 'Change Password'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
